import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@ngneat/reactive-forms';
import { DETAIL_EXPANDED } from '../../animations/detail-expanded';
import { UiAutoSizeDirective } from '../../directives/auto-size/auto-size.directive';
import { UiTableFormOrchestratorDirective } from '../../directives/table-form-ochestrator/table-form-ochestrator.directive';
import {
  FormOrchestratorTableData,
  FormTableColumns,
} from '../../models/table.models';
import { FeaturesMediaIfDirective } from '@brunsker/libs/core/features/media-if/src';

@Component({
  selector: 'bs-form-table',
  templateUrl: './form-table.component.html',
  styleUrls: ['./form-table.component.scss', '../table/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [DETAIL_EXPANDED],
})
export class UiFormTableComponent<
  T extends Record<string, any>,
> implements OnChanges {
  @ViewChild(FeaturesMediaIfDirective, { static: true })
  readonly mediaIfDirective!: FeaturesMediaIfDirective<number>;
  @ViewChild(UiTableFormOrchestratorDirective, { static: true })
  readonly bsFormOrchestrator!: UiTableFormOrchestratorDirective<T>;
  @ViewChildren(UiAutoSizeDirective)
  readonly autoSizeDirective!: QueryList<UiAutoSizeDirective>;
  @ViewChildren('actionCellViewContainerRef', { read: ViewContainerRef })
  readonly actionQueryList!: QueryList<ViewContainerRef>;
  readonly packagingSelectConfig = { controlName: 'tipoEmbalagem', options: [] };

  @Input() noImage!: string;
  @Output() eventAction = new EventEmitter<T>();

  @ViewChildren('expandedDetailViewContainerRef', { read: ViewContainerRef })
  readonly expandedDetailQueryList!: QueryList<ViewContainerRef>;
  @Input() displayColumns!: FormTableColumns[];
  @Input() formGroup!: FormGroup<T>;
  @Input() dataSource!: FormOrchestratorTableData<T>[];
  @Input() expandedElementViewMedia!: number;
  @Input() idPropertyName = 'id';
  @Input() hasAction!: boolean;
  @Input() actionPosition: 'left' | 'right' = 'right';
  @Input() keyTable: string = 'keyForm';

  private originalDataSource: FormOrchestratorTableData<T>[] = [];

  sortState: { column: string | null; direction: 'asc' | 'desc' | null } = {
    column: null,
    direction: null,
  };

  constructor(readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] && this.dataSource) {
      this.originalDataSource = [...this.dataSource];
      if (this.sortState.column && this.sortState.direction) {
        this.applySort();
      }
    }
  }

  onHeaderClick(column: string): void {
    if (this.sortState.column !== column) {
      this.sortState = { column, direction: 'asc' };
    } else if (this.sortState.direction === 'asc') {
      this.sortState = { column, direction: 'desc' };
    } else {
      this.sortState = { column: null, direction: null };
    }
    this.applySort();
  }

  private applySort(): void {
    let sorted: FormOrchestratorTableData<T>[];

    if (!this.sortState.column || !this.sortState.direction) {
      sorted = [...this.originalDataSource];
    } else {
      const col = this.sortState.column;
      const dir = this.sortState.direction;
      sorted = [...this.originalDataSource].sort((a: any, b: any) => {
        const aVal = (a[col] ?? '').toString().toLowerCase();
        const bVal = (b[col] ?? '').toString().toLowerCase();
        if (aVal < bVal) return dir === 'asc' ? -1 : 1;
        if (aVal > bVal) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    this.dataSource = sorted;

    if (this.bsFormOrchestrator) {
      this.bsFormOrchestrator.formOrchestratorData = sorted;
      this.bsFormOrchestrator.tableOrchestratorDataSource =
        sorted as unknown as T[];
      if (this.bsFormOrchestrator.matTableDataSource) {
        this.bsFormOrchestrator.matTableDataSource.data =
          sorted as unknown as T[];
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  getIndex(column: string, group: any[], index: number): number {
    if (column === 'incrementor') group.findIndex((item) => item.incrementor);
    return index;
  }

  getFormControlValue(element: FormOrchestratorTableData<T>, index: number) {
    const controlName = this.displayColumns[index]?.columnFormControlName;
    return element.formGroupValue.find(
      (control) => control.formControlName === controlName,
    );
  }

  onSelectChange(
    element: FormOrchestratorTableData<T>,
    index: number,
    value: unknown,
  ): void {
    this.getFormControlValue(element, index)?.onSelectionChange?.(value);
  }
}
